import type { LearningExercise } from "../types";

// EQE-Modul: EPÜ Einspruch & Beschwerde (Sprint E1)
// Abdeckung: Art. 99–105c EPÜ (Einspruch), Art. 106–111 EPÜ (Beschwerde),
//            R. 76/77/79/81/99/103 EPÜ-AO, Art. 112/112a EPÜ (Große Beschwerdekammer),
//            G 9/92 / G 1/99 (reformatio in peius).
// pathId "epu-eqe", mode "ausbildung", reviewStatus "draft" — Freigabe durch question-reviewer.
// Format: options: string[], answers: number[] (Standardformat wie hagen-stellvertretung.ts).
// IDs: eqe-einspruch-001 … eqe-einspruch-040
export const eqeEinspruchBeschwerde: LearningExercise[] = [

  // ─── MC SINGLE — Einspruch ─────────────────────────────────────────────────

  {
    id: "eqe-einspruch-001",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruch (Art. 99)",
    title: "Einspruchsfrist Art. 99 EPÜ",
    prompt:
      "Innerhalb welcher Frist muss ein Einspruch gegen ein europäisches Patent eingelegt werden, und wann beginnt diese Frist?",
    options: [
      "6 Monate ab dem Tag der Erteilung des Patents.",
      "9 Monate ab dem Tag der Bekanntmachung des Hinweises auf die Erteilung im Europäischen Patentblatt.",
      "12 Monate ab dem Anmeldetag des Patents.",
      "9 Monate ab dem Tag der Zustellung der Patenterteilung an den Patentinhaber.",
    ],
    answers: [1],
    explanation:
      "Art. 99 Abs. 1 EPÜ: Innerhalb von neun Monaten nach der Bekanntmachung des Hinweises auf die Erteilung des europäischen Patents im Europäischen Patentblatt kann jeder beim Europäischen Patentamt gegen dieses Patent Einspruch einlegen. Maßgeblich ist die Bekanntmachung im Patentblatt, nicht der Erteilungstag oder die Zustellung an den Inhaber.",
    sourceIds: ["art99epc"],
    sources: [
      {
        id: "art99epc",
        label: "Art. 99 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a99.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["einspruch", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-002",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruch (Art. 99)",
    title: "Einspruchsberechtigung",
    prompt:
      "Wer ist zur Einlegung eines Einspruchs gegen ein europäisches Patent berechtigt?",
    options: [
      "Nur natürliche oder juristische Personen, die ein wirtschaftliches Interesse am Widerruf nachweisen können.",
      "Nur Dritte, die kein eigenes Schutzrecht angemeldet haben.",
      "Jede Person (jedermann), mit Ausnahme des Patentinhabers selbst.",
      "Nur Personen, die in einem der Vertragsstaaten des EPÜ ansässig sind.",
    ],
    answers: [2],
    explanation:
      "Art. 99 Abs. 1 EPÜ: 'kann jeder' Einspruch einlegen — der Einspruch ist ein Popularrechtsbehelf, an den kein Nachweis eines eigenen Interesses geknüpft ist. Ausgenommen ist nach der Rechtsprechung des EPA jedoch der Patentinhaber selbst (kein Einspruch gegen das eigene Patent; vgl. G 9/93).",
    sourceIds: ["art99epc"],
    sources: [
      {
        id: "art99epc",
        label: "Art. 99 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a99.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["einspruch", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-003",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruchsgründe (Art. 100)",
    title: "Einspruchsgründe im Überblick",
    prompt:
      "Welche Einspruchsgründe sind nach Art. 100 EPÜ abschließend anerkannt?",
    options: [
      "Nur mangelnde Neuheit und mangelnde erfinderische Tätigkeit.",
      "a) mangelnde Patentierbarkeit (Art. 52–57 EPÜ), b) mangelnde Ausführbarkeit (Art. 83 EPÜ) und c) unzulässige Erweiterung gegenüber der Anmeldung (Art. 123 Abs. 2 EPÜ) bzw. gegenüber der Stammanmeldung (Art. 76 Abs. 1 EPÜ).",
      "a) mangelnde Neuheit, b) mangelnde Ausführbarkeit und c) unzulässige Erweiterung gegenüber der Anmeldung — erfinderische Tätigkeit ist kein eigenständiger Einspruchsgrund.",
      "a) mangelnde Patentierbarkeit, b) mangelnde Ausführbarkeit, c) unzulässige Erweiterung und d) Verletzung von Prioritätsrechten Dritter.",
    ],
    answers: [1],
    explanation:
      "Art. 100 EPÜ listet abschließend drei Einspruchsgründe: (a) der Gegenstand des Patents ist nach Art. 52–57 EPÜ nicht patentierbar; (b) das Patent offenbart die Erfindung nicht so deutlich und vollständig, dass ein Fachmann sie ausführen kann (Art. 83 EPÜ); (c) der Gegenstand des Patents geht über den Inhalt der Anmeldung in der eingereichten Fassung oder bei einer Teilanmeldung über den Inhalt der früheren Anmeldung hinaus (Art. 76 Abs. 1 EPÜ). Erfinderische Tätigkeit ist Teil von Grund (a) über Art. 56 EPÜ.",
    sourceIds: ["art100epc"],
    sources: [
      {
        id: "art100epc",
        label: "Art. 100 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a100.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["einspruch", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-004",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruchsgründe (Art. 100)",
    title: "Einspruchsgrund Art. 100(b) — Ausführbarkeit",
    prompt:
      "Ein Einspruchführer möchte das Patent auf Grundlage von Art. 100(b) EPÜ angreifen. Welche Behauptung muss er stützen?",
    options: [
      "Das Patent sei durch Einbeziehung von Merkmalen aus der Beschreibung unzulässig erweitert worden.",
      "Der Fachmann könne die Erfindung anhand der Offenbarung des Patents nicht so ausführen, dass der gesamte Schutzbereich des Anspruchs reproduzierbar sei (Art. 83 EPÜ).",
      "Die im Patent beschriebene Erfindung sei bereits vor dem Anmeldetag offenbart worden.",
      "Die Erfindung beruhe nicht auf erfinderischer Tätigkeit gegenüber dem nächstliegenden Stand der Technik.",
    ],
    answers: [1],
    explanation:
      "Art. 100(b) EPÜ verweist auf Art. 83 EPÜ: Die europäische Patentanmeldung muss die Erfindung so deutlich und vollständig offenbaren, dass ein Fachmann sie ausführen kann. Im Einspruch trägt der Einsprechende die Darlegungs- und Beweislast dafür, dass die Offenbarung für den gesamten beanspruchten Bereich unzureichend ist. Art. 100(a) betrifft Patentierbarkeit (inkl. Neuheit und erf. Tätigkeit), Art. 100(c) unzulässige Erweiterung.",
    sourceIds: ["art100epc", "art83epc"],
    sources: [
      {
        id: "art100epc",
        label: "Art. 100 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a100.html",
      },
      {
        id: "art83epc",
        label: "Art. 83 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a83.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["einspruch", "epc", "claim-analysis"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-005",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruch Inhalt (R. 76 EPÜ-AO)",
    title: "Mindestinhalt des Einspruchs (R. 76 EPÜ-AO)",
    prompt:
      "Welche Angaben muss ein Einspruch nach R. 76 EPÜ-AO mindestens enthalten, damit er als zulässig angesehen werden kann?",
    options: [
      "Nur die Angabe des Patents, gegen das Einspruch eingelegt wird, und die Zahlung der Einspruchsgebühr.",
      "Angabe des Patents, Erklärung über den Umfang des Einspruchs, die geltend gemachten Einspruchsgründe nach Art. 100 EPÜ sowie die zur Begründung dienenden Tatsachen und Beweismittel.",
      "Nur die Angabe des Patents und der Name des Einsprechenden; eine Begründung kann nachgereicht werden.",
      "Angabe des Patents, Einspruchsgründe und ein vollständiges Gutachten eines zugelassenen Patentanwalts.",
    ],
    answers: [1],
    explanation:
      "R. 76 Abs. 2 EPÜ-AO: Der Einspruch muss enthalten: (a) die Bezeichnung des Patents, gegen das Einspruch eingelegt wird; (b) Name und Anschrift des Einsprechenden; (c) eine Erklärung über den Umfang des Einspruchs; (d) eine Erklärung über die Einspruchsgründe und die zur Begründung dienenden Tatsachen und Beweismittel. Fehlen wesentliche Angaben und werden sie nicht fristgerecht nachgereicht, ist der Einspruch nach R. 77 EPÜ-AO als unzulässig zu verwerfen.",
    sourceIds: ["r76epc"],
    sources: [
      {
        id: "r76epc",
        label: "R. 76 EPÜ-AO",
        url: "https://www.epo.org/en/legal/epc/2020/r76.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["einspruch", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-006",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruchsverfahren (Art. 101/102)",
    title: "Entscheidung der Einspruchsabteilung",
    prompt:
      "Zu welchen Ergebnissen kann die Einspruchsabteilung nach Prüfung der Einspruchsgründe kommen (Art. 101/102 EPÜ)?",
    options: [
      "Entweder vollständiger Widerruf oder vollständige Zurückweisung des Einspruchs — eine Aufrechterhaltung in geänderter Form ist nicht möglich.",
      "Widerruf des Patents, Aufrechterhaltung des Patents in geändertem Umfang oder Zurückweisung des Einspruchs (Patent bleibt unverändert aufrechterhalten).",
      "Nur Widerruf oder unveränderte Aufrechterhaltung; Beschränkungsanträge sind ausschließlich im Beschränkungsverfahren nach Art. 105a EPÜ zu stellen.",
      "Zurückverweisung der Sache an die Prüfungsabteilung zur erneuten Erteilung.",
    ],
    answers: [1],
    explanation:
      "Art. 101 Abs. 3 EPÜ i.V.m. Art. 102 EPÜ: Die Einspruchsabteilung entscheidet, ob das Patent widerrufen wird (Art. 102 Abs. 1 EPÜ), in geändertem Umfang aufrechterhalten wird (Art. 102 Abs. 3 EPÜ) oder der Einspruch zurückgewiesen wird, d.h. das Patent unverändert aufrechterhalten bleibt (Art. 102 Abs. 2 EPÜ). Eine Aufrechterhaltung in geändertem Umfang erfordert die Zustimmung des Patentinhabers zu den Änderungen und erneute Zahlung einer Druckgebühr.",
    sourceIds: ["art101epc", "art102epc"],
    sources: [
      {
        id: "art101epc",
        label: "Art. 101 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a101.html",
      },
      {
        id: "art102epc",
        label: "Art. 102 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a102.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["einspruch", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-007",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beitritt (Art. 105)",
    title: "Beitritt zum Einspruchsverfahren (Art. 105 EPÜ)",
    prompt:
      "Unter welchen Voraussetzungen kann ein Dritter nach Art. 105 EPÜ dem laufenden Einspruchsverfahren beitreten?",
    options: [
      "Jederzeit, solange das Einspruchsverfahren noch nicht abgeschlossen ist, ohne besondere Voraussetzungen.",
      "Nur, wenn er selbst innerhalb der 9-Monats-Frist Einspruch eingelegt hat.",
      "Wenn er nachweist, dass gegen ihn eine Verletzungsklage erhoben wurde oder er nach Ablauf der Einspruchsfrist aufgefordert wurde, eine behauptete Patentverletzung zu unterlassen, und er innerhalb von 3 Monaten nach diesem Datum beitritt.",
      "Nur mit Zustimmung aller Parteien und des EPA.",
    ],
    answers: [2],
    explanation:
      "Art. 105 Abs. 1 EPÜ: Nach Ablauf der Einspruchsfrist kann jeder Dritte dem Einspruchsverfahren beitreten, wenn er nachweist, dass (a) gegen ihn eine Klage wegen Verletzung des Patents erhoben wurde, oder (b) er nach Zurückweisung eines Angriffs wegen Verletzung auf Unterlassen in Anspruch genommen wurde. Der Beitritt muss innerhalb von drei Monaten nach dem Datum eingereicht werden, an dem die Klage erhoben wurde oder die Aufforderung ergangen ist (Art. 105 Abs. 2 EPÜ).",
    sourceIds: ["art105epc"],
    sources: [
      {
        id: "art105epc",
        label: "Art. 105 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a105.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["einspruch", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-008",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschränkung/Widerruf auf Antrag (Art. 105a–c)",
    title: "Beschränkungsverfahren Art. 105a EPÜ",
    prompt:
      "Was ist das Beschränkungs- und Widerrufsverfahren nach Art. 105a EPÜ, und wer kann es einleiten?",
    options: [
      "Ein Verfahren, das jeder Dritte nach Erteilung zur Beschränkung des Schutzumfangs einleiten kann.",
      "Ein vom Patentinhaber selbst eingeleitetes Verfahren, in dem er beim EPA entweder die Beschränkung des Patents durch Änderung der Ansprüche oder den vollständigen Widerruf des Patents beantragen kann.",
      "Ein Verfahren, das nur während eines laufenden Einspruchsverfahrens möglich ist.",
      "Ein Rechtsmittelverfahren gegen die Entscheidung der Einspruchsabteilung.",
    ],
    answers: [1],
    explanation:
      "Art. 105a Abs. 1 EPÜ: Auf Antrag des Patentinhabers kann das EPA das europäische Patent beschränken oder widerrufen. Dieses Verfahren steht ausschließlich dem Patentinhaber zu. Die Beschränkung erfolgt durch Änderung der Ansprüche; die Beschränkung wirkt ab dem Tag der Bekanntmachung im Patentblatt (Art. 105b Abs. 3 EPÜ). Das Verfahren ist außerdem nicht parallel zu einem laufenden Einspruchsverfahren möglich (Art. 105a Abs. 2 EPÜ).",
    sourceIds: ["art105aepc"],
    sources: [
      {
        id: "art105aepc",
        label: "Art. 105a EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a105a.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["einspruch", "epc"],
    // FIX (question-reviewer 2026-06-03): Sub-Zitat korrigiert — Wirkung der Beschränkung
    // ab Bekanntmachung folgt aus Art. 105b Abs. 3 EPÜ (war fälschlich Abs. 2). Inhalt
    // (nur Patentinhaber, nicht parallel zum Einspruch, Anspruchsänderung) korrekt.
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE — Fallkonstellationen Einspruch ────────────────────────────

  {
    id: "eqe-einspruch-009",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruch (Art. 100) — Fallkonstellation",
    title: "Fall: Welcher Einspruchsgrund greift?",
    prompt:
      "Der Einsprechende legt ein Dokument D1 vor, das die Erfindung gemäß Anspruch 1 vollständig vorwegnimmt (jedes Merkmal von Anspruch 1 findet sich wörtlich in D1). Er stützt seinen Einspruch allein auf dieses Dokument. Welcher Einspruchsgrund nach Art. 100 EPÜ ist einschlägig?",
    options: [
      "Art. 100(b) EPÜ, weil die Offenbarung der Erfindung nicht ausreichend ist.",
      "Art. 100(c) EPÜ, weil der Gegenstand des Patents über den Inhalt der Anmeldung hinausgeht.",
      "Art. 100(a) EPÜ i.V.m. Art. 54 EPÜ (mangelnde Neuheit), weil D1 sämtliche Merkmale des Anspruchs 1 offenbart.",
      "Art. 100(a) EPÜ i.V.m. Art. 56 EPÜ (mangelnde erfinderische Tätigkeit), weil D1 der nächstliegende Stand der Technik ist.",
    ],
    answers: [2],
    explanation:
      "Art. 100(a) EPÜ i.V.m. Art. 54 EPÜ (Neuheit): Offenbart ein einziges Dokument D1 vollständig alle Merkmale des Anspruchs, fehlt dem Patent die Neuheit. Neuheit und erfinderische Tätigkeit sind beide Unterfälle von Art. 100(a) EPÜ, beziehen sich aber auf unterschiedliche materiellrechtliche Standards. Mangelnde Neuheit liegt vor, wenn ein Einzeldokument den gesamten Anspruch antizipiert. Mangelnde erfinderische Tätigkeit erfordert die Kombination von Dokumenten und eine Analyse des Naheliegens für den Fachmann.",
    sourceIds: ["art100epc", "art54epc"],
    sources: [
      {
        id: "art100epc",
        label: "Art. 100 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a100.html",
      },
      {
        id: "art54epc",
        label: "Art. 54 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a54.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["einspruch", "neuheit", "epc", "claim-analysis"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-010",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruch — Fallkonstellation",
    title: "Fall: Fristberechnung Einspruch",
    prompt:
      "Der Hinweis auf die Erteilung des europäischen Patents EP 123 wird am 15. März 2025 im Europäischen Patentblatt bekannt gemacht. Ein Mitbewerber möchte Einspruch einlegen. Was ist der späteste Termin für den Eingang des vollständigen Einspruchs inklusive Einspruchsgebühr beim EPA?",
    options: [
      "15. September 2025 (6 Monate ab Bekanntmachung).",
      "14. März 2026 (12 Monate ab Bekanntmachung).",
      "15. Dezember 2025 (9 Monate ab Bekanntmachung).",
      "15. März 2026 (12 Monate ab Anmeldetag).",
    ],
    answers: [2],
    explanation:
      "Art. 99 Abs. 1 EPÜ: Die Einspruchsfrist beträgt neun Monate ab Bekanntmachung im Patentblatt. Startdatum: 15. März 2025. Neun Monate später: 15. Dezember 2025. Die Einspruchsgebühr (R. 6 EPÜ-AO) und der vollständige Einspruch müssen bis zu diesem Tag beim EPA eingegangen sein; Postaufgabe genügt nicht (Zugangsprinzip). Eine Verlängerung dieser Frist ist nicht möglich.",
    sourceIds: ["art99epc"],
    sources: [
      {
        id: "art99epc",
        label: "Art. 99 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a99.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["einspruch", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-011",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Einspruch (Art. 100(c)) — Fallkonstellation",
    title: "Fall: Art. 100(c) — unzulässige Erweiterung im Einspruch",
    prompt:
      "Anspruch 1 in der erteilten Fassung beschreibt eine Verbindung mit Merkmal A, B und C. In der ursprünglich eingereichten Anmeldung war Merkmal C nur als Teil einer spezifischen Kombination A+B+C+D offenbart; das isolierte Merkmal C war nirgends einzeln erwähnt. Welcher Einspruchsgrund greift?",
    options: [
      "Art. 100(b) EPÜ: Merkmal C ist für den Fachmann nicht ausführbar beschrieben.",
      "Art. 100(a) EPÜ i.V.m. Art. 54 EPÜ: Das Merkmal C war im Stand der Technik bekannt.",
      "Art. 100(c) EPÜ i.V.m. Art. 123 Abs. 2 EPÜ: Die isolierte Beanspruchung von C ohne D stellt eine unzulässige Erweiterung dar (Zwischenverallgemeinerung).",
      "Art. 100(a) EPÜ i.V.m. Art. 56 EPÜ: Die Auswahl von Merkmal C aus der Kombination beruht nicht auf erfinderischer Tätigkeit.",
    ],
    answers: [2],
    explanation:
      "Art. 100(c) EPÜ i.V.m. Art. 123 Abs. 2 EPÜ: Wird ein ursprünglich nur in Kombination A+B+C+D offenbartes Merkmal C isoliert in den Anspruch aufgenommen (ohne D), entsteht eine unzulässige Zwischenverallgemeinerung — der beanspruchte Gegenstand (A+B+C ohne D) war in der Anmeldung als eingereicht nicht als solcher offenbart. Dies ist das klassische Szenario für Art. 100(c) EPÜ, der auf Art. 123 Abs. 2 EPÜ (sowie Art. 76 Abs. 1 für Teilanmeldungen) verweist.",
    sourceIds: ["art100epc", "art123epc"],
    sources: [
      {
        id: "art100epc",
        label: "Art. 100 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a100.html",
      },
      {
        id: "art123epc",
        label: "Art. 123 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a123.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["einspruch", "epc", "claim-analysis"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE — Beschwerde ────────────────────────────────────────────────

  {
    id: "eqe-einspruch-012",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde (Art. 106/108)",
    title: "Fristen der Beschwerde (Art. 108 EPÜ)",
    prompt:
      "Welche Fristen gelten nach Art. 108 EPÜ für die Einlegung der Beschwerde und die Vorlage der Beschwerdebegründung?",
    options: [
      "Beschwerde: 1 Monat; Begründung: 3 Monate — jeweils ab Zustellung der Entscheidung.",
      "Beschwerde: 2 Monate; Begründung: 4 Monate — jeweils ab Zustellung der Entscheidung.",
      "Beschwerde: 3 Monate; Begründung: 6 Monate — jeweils ab Zustellung der Entscheidung.",
      "Beschwerde: 2 Monate ab Zustellung; Begründung: 2 Monate ab Einlegung der Beschwerde.",
    ],
    answers: [1],
    explanation:
      "Art. 108 EPÜ: Die Beschwerde ist innerhalb von zwei Monaten nach Zustellung der Entscheidung schriftlich einzulegen. Die Beschwerdebegründung ist innerhalb von vier Monaten nach Zustellung der Entscheidung schriftlich einzureichen. Beide Fristen beginnen mit der Zustellung der angefochtenen Entscheidung und sind nicht verlängerbar. Fehlt die Begründung, gilt die Beschwerde als nicht eingelegt (R. 101 Abs. 1 EPÜ-AO).",
    sourceIds: ["art108epc"],
    sources: [
      {
        id: "art108epc",
        label: "Art. 108 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a108.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-013",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde (Art. 106)",
    title: "Devolutiv- und Suspensiveffekt der Beschwerde",
    prompt:
      "Welche Rechtswirkungen hat eine zulässige Beschwerde gegen eine Entscheidung einer erstinstanzlichen Abteilung des EPA?",
    options: [
      "Die Beschwerde hat nur Suspensiveffekt: Sie hemmt die Vollziehung der Entscheidung, gibt die Sache aber nicht an die Beschwerdekammer ab.",
      "Die Beschwerde hat Devolutiveffekt (die Sache geht an die Beschwerdekammer über) und Suspensiveffekt (die angefochtene Entscheidung wird nicht vollzogen).",
      "Die Beschwerde hat nur Devolutiveffekt: Die Sache geht an die Beschwerdekammer über, ohne dass die Vollziehung der Entscheidung gehemmt wird.",
      "Die Beschwerde bewirkt automatisch die Zurückverweisung an die Prüfungsabteilung.",
    ],
    answers: [1],
    explanation:
      "Art. 106 Abs. 1 EPÜ: Die Beschwerde hat aufschiebende Wirkung (Suspensiveffekt) — die angefochtene Entscheidung wird bis zur Entscheidung über die Beschwerde nicht vollzogen. Gleichzeitig bewirkt sie den Devolutiveffekt: Die sachliche Zuständigkeit für die Entscheidung wechselt auf die Beschwerdekammer. Beide Effekte sind kumulative Grundwirkungen jeder zulässigen Beschwerde im EPÜ-System.",
    sourceIds: ["art106epc"],
    sources: [
      {
        id: "art106epc",
        label: "Art. 106 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a106.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-014",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde (Art. 107)",
    title: "Beschwerdeberechtigung (Art. 107 EPÜ)",
    prompt:
      "Wer ist nach Art. 107 EPÜ zur Einlegung einer Beschwerde berechtigt?",
    options: [
      "Jede natürliche oder juristische Person, die ein rechtliches Interesse an der Aufhebung der Entscheidung hat.",
      "Nur der Patentinhaber oder Anmelder.",
      "Jede Partei des Verfahrens, soweit sie durch die Entscheidung beschwert ist.",
      "Nur der Einsprechende, nicht der Patentinhaber.",
    ],
    answers: [2],
    explanation:
      "Art. 107 S. 1 EPÜ: Jede Partei eines Verfahrens, die durch eine Entscheidung beschwert ist, kann Beschwerde einlegen. Beschwerdebefugnis setzt also zweierlei voraus: (1) Parteistellung im vorangegangenen Verfahren und (2) Beschwer durch die Entscheidung (d.h. die Entscheidung geht dem Beschwerdeführer ganz oder teilweise zu Lasten). Nicht beschwert ist eine Partei, die mit ihrem gesamten Antrag Erfolg hatte.",
    sourceIds: ["art107epc"],
    sources: [
      {
        id: "art107epc",
        label: "Art. 107 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a107.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-015",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde (Art. 109)",
    title: "Abhilfeverfahren (Art. 109 EPÜ)",
    prompt:
      "Was geschieht, wenn die Abteilung, die eine angefochtene Entscheidung erlassen hat, die Beschwerde für begründet hält (Art. 109 EPÜ)?",
    options: [
      "Sie leitet die Beschwerde unverzüglich an die Beschwerdekammer weiter, ohne eigene Prüfung vorzunehmen.",
      "Sie hat der Beschwerde abzuhelfen (Selbsthilfe/Abhilfe), wenn alle Parteien zustimmen; fehlt die Zustimmung, wird vorgelegt.",
      "Sie hat der Beschwerde innerhalb von drei Monaten abzuhelfen, indem sie ihre Entscheidung aufhebt und entsprechend abändert; andernfalls ist die Beschwerde unverzüglich der Beschwerdekammer vorzulegen.",
      "Sie übermittelt die Akte ohne eigene Abhilfemöglichkeit direkt an die Beschwerdekammer.",
    ],
    answers: [2],
    explanation:
      "Art. 109 Abs. 1 EPÜ: Hält die Abteilung, die die angefochtene Entscheidung erlassen hat, die Beschwerde für zulässig und begründet, hat sie ihr abzuhelfen; diese Selbsthilfemöglichkeit (Abhilfe) muss innerhalb von drei Monaten nach Eingang der Beschwerdebegründung erfolgen. Hilft sie nicht ab, ist die Beschwerde unverzüglich der Beschwerdekammer vorzulegen (Art. 109 Abs. 2 EPÜ). Die Zustimmung aller Parteien ist keine Voraussetzung.",
    sourceIds: ["art109epc"],
    sources: [
      {
        id: "art109epc",
        label: "Art. 109 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a109.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-016",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde (Art. 111)",
    title: "Entscheidungsbefugnis der Beschwerdekammer (Art. 111 EPÜ)",
    prompt:
      "Welche Entscheidungsoptionen stehen der Beschwerdekammer nach Art. 111 EPÜ offen?",
    options: [
      "Nur Aufhebung der Entscheidung der Vorinstanz oder Zurückweisung der Beschwerde.",
      "Aufhebung und Entscheidung in der Sache selbst oder Zurückverweisung an die Vorinstanz zur weiteren Sachbehandlung, jeweils mit oder ohne bindenden Rechtsausführungen.",
      "Ausschließlich Zurückverweisung an die Vorinstanz ohne eigene Sachentscheidung.",
      "Vorlage an die Große Beschwerdekammer als einzig mögliche Entscheidung bei Rechtsfragen.",
    ],
    answers: [1],
    explanation:
      "Art. 111 Abs. 1 EPÜ: Die Beschwerdekammer kann entweder in der Sache selbst entscheiden oder die Angelegenheit an die Vorinstanz zurückverweisen. Art. 111 Abs. 2 EPÜ: Bei Zurückverweisung ist die Vorinstanz an die rechtliche Beurteilung der Beschwerdekammer gebunden, soweit der Sachverhalt derselbe ist. Die Beschwerdekammer kann also vollumfänglich reformieren (Aufhebung + eigene Sachentscheidung) oder kassieren und zurückverweisen.",
    sourceIds: ["art111epc"],
    sources: [
      {
        id: "art111epc",
        label: "Art. 111 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a111.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-017",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde (R. 103 EPÜ-AO)",
    title: "Rückzahlung der Beschwerdegebühr (R. 103 EPÜ-AO)",
    prompt:
      "Unter welcher Voraussetzung wird die Beschwerdegebühr nach R. 103 EPÜ-AO vollständig zurückgezahlt?",
    options: [
      "Immer, wenn die Beschwerdekammer der Beschwerde stattgibt.",
      "Wenn das Abhilfeverfahren nach Art. 109 EPÜ zur Stattgabe der Beschwerde führt oder die Beschwerdekammer die Rückzahlung wegen eines wesentlichen Verfahrensmangels anordnet.",
      "Wenn der Beschwerdeführer die Beschwerde innerhalb von 2 Monaten nach Einlegung zurücknimmt.",
      "Nur wenn die Beschwerde als unzulässig verworfen wird.",
    ],
    answers: [1],
    explanation:
      "R. 103 Abs. 1 EPÜ-AO sieht eine vollständige Rückzahlung der Beschwerdegebühr vor in zwei Fällen: (a) wenn der Beschwerde nach Art. 109 EPÜ im Abhilfeverfahren stattgegeben wird (da die Kammer dann gar nicht tätig werden musste), oder (b) wenn die Beschwerdekammer die Rückzahlung wegen eines wesentlichen Verfahrensmangels im erstinstanzlichen Verfahren für angemessen hält. R. 103 Abs. 4 EPÜ-AO sieht zudem bei rechtzeitiger Rücknahme eine Teilrückzahlung (25 %) vor.",
    sourceIds: ["r103epc"],
    sources: [
      {
        id: "r103epc",
        label: "R. 103 EPÜ-AO",
        url: "https://www.epo.org/en/legal/epc/2020/r103.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE — Reformatio in peius ──────────────────────────────────────

  {
    id: "eqe-einspruch-018",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde — reformatio in peius (G 9/92, G 1/99)",
    title: "Reformatio in peius — Grundregel (G 9/92)",
    prompt:
      "Nur der Patentinhaber hat gegen die Entscheidung der Einspruchsabteilung Beschwerde eingelegt; der Einsprechende hat keine Anschlussbeschwerde eingelegt. Was gilt für den Umfang der Beschwerdekammerentscheidung (reformatio in peius)?",
    options: [
      "Die Beschwerdekammer ist frei, das Patent in einem für den Patentinhaber schlechteren Umfang aufrechtzuerhalten, als es die Einspruchsabteilung getan hat.",
      "Die Beschwerdekammer darf das Patent nicht in einem für den Patentinhaber ungünstigeren Umfang aufrechterhalten, als es die Einspruchsabteilung in der angefochtenen Entscheidung getan hat (Grundsatz: keine reformatio in peius, G 9/92).",
      "Die Beschwerdekammer muss das Patent vollständig widerrufen, wenn nur eine Partei Beschwerde eingelegt hat.",
      "Die Beschwerdekammer darf die Entscheidung der Einspruchsabteilung in Abwesenheit eines Beschwerdegegners gar nicht abändern.",
    ],
    answers: [1],
    explanation:
      "G 9/92 (Große Beschwerdekammer): Im Einspruchsbeschwerdeverfahren gilt der Grundsatz, dass die Beschwerdekammer, wenn nur eine Partei Beschwerde eingelegt hat, das Patent nicht in einem Umfang aufrechterhalten darf, der für den Beschwerdeführer ungünstiger ist als die angefochtene Entscheidung. Legt nur der Patentinhaber Beschwerde ein, darf die Kammer das Patent nicht stärker beschränken. G 1/99 betrifft den UMGEKEHRTEN Fall — der Einsprechende ist alleiniger Beschwerdeführer — und lässt dort dem Patentinhaber (Beschwerdegegner) ausnahmsweise eine an sich verbotene, den Einsprechenden schlechterstellende Änderung zu, jedoch nur in gestufter Reihenfolge (zuerst ein beschränkendes Merkmal, nur als letztes Mittel die Streichung des unzulässigen Merkmals).",
    sourceIds: ["art106epc"],
    sources: [
      {
        id: "art106epc",
        label: "Art. 106 EPÜ / G 9/92",
        url: "https://www.epo.org/en/legal/epc/2020/a106.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["beschwerde", "epc"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-019",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde — Fallkonstellation",
    title: "Fall: Zulässigkeit der Beschwerde — Beschwerdebegründung",
    prompt:
      "Der Einsprechende legt am Tag der Zustellung der Entscheidung der Einspruchsabteilung Beschwerde ein und zahlt die Beschwerdegebühr. Die Beschwerdebegründung reicht er jedoch erst 5 Monate nach der Zustellung ein. Was ist die Rechtsfolge?",
    options: [
      "Die Beschwerde ist zulässig, weil die Gebühr fristgerecht gezahlt wurde; die verspätete Begründung kann nachgebessert werden.",
      "Die Beschwerde gilt als nicht eingelegt, weil die Beschwerdebegründung innerhalb von vier Monaten nach Zustellung hätte eingereicht werden müssen (Art. 108 EPÜ).",
      "Die Beschwerdekammer verlängert die Frist auf Antrag des Einsprechenden automatisch.",
      "Die Beschwerde ist unzulässig und die Beschwerdegebühr wird einbehalten; der Einsprechende kann jedoch eine neue Beschwerde innerhalb der Restfrist einlegen.",
    ],
    answers: [1],
    explanation:
      "Art. 108 EPÜ: Die Beschwerdebegründung ist innerhalb von vier Monaten nach Zustellung der angefochtenen Entscheidung einzureichen. Diese Frist ist nicht verlängerbar. R. 101 Abs. 1 EPÜ-AO: Wird die Begründung nicht rechtzeitig eingereicht, gilt die Beschwerde als nicht eingelegt (= die Entscheidung der Einspruchsabteilung wird rechtskräftig). Die Beschwerdegebühr ist in diesem Fall zurückzuzahlen.",
    sourceIds: ["art108epc"],
    sources: [
      {
        id: "art108epc",
        label: "Art. 108 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a108.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE — Große Beschwerdekammer ───────────────────────────────────

  {
    id: "eqe-einspruch-020",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Große Beschwerdekammer (Art. 112)",
    title: "Vorlage an die Große Beschwerdekammer (Art. 112 EPÜ)",
    prompt:
      "Unter welchen Voraussetzungen kann eine Beschwerdekammer eine Rechtsfrage der Großen Beschwerdekammer zur Entscheidung vorlegen?",
    options: [
      "Jederzeit nach Ermessen der Beschwerdekammer, wenn sie eine Klärung für sinnvoll hält.",
      "Nur wenn alle Parteien des Beschwerdeverfahrens der Vorlage zustimmen.",
      "Wenn eine Entscheidung über eine Rechtsfrage von grundsätzlicher Bedeutung erforderlich ist oder wenn die Kammer von einer Entscheidung einer anderen Beschwerdekammer abweichen will (Art. 112 Abs. 1(a) EPÜ), oder auf Antrag des EPA-Präsidenten (Art. 112 Abs. 1(b) EPÜ).",
      "Nur auf Antrag des Patentinhabers oder Anmelders.",
    ],
    answers: [2],
    explanation:
      "Art. 112 Abs. 1(a) EPÜ: Eine Beschwerdekammer legt der Großen Beschwerdekammer eine Rechtsfrage vor, wenn dies zur Sicherung einer einheitlichen Anwendung des Rechts oder wenn eine Frage von grundsätzlicher Bedeutung zu entscheiden ist — insbesondere wenn die Kammer von einer früheren Entscheidung einer anderen Beschwerdekammer abweichen will. Art. 112 Abs. 1(b) EPÜ: Auch der Präsident des EPA kann dem Gremium Rechtsfragen zur Stellungnahme vorlegen (Präsidentenvorlage).",
    sourceIds: ["art112epc"],
    sources: [
      {
        id: "art112epc",
        label: "Art. 112 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a112.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-021",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Überprüfungsantrag (Art. 112a)",
    title: "Überprüfungsantrag Art. 112a EPÜ",
    prompt:
      "Wofür ist der Überprüfungsantrag nach Art. 112a EPÜ vorgesehen, und auf welche Verfahrensmängel ist er beschränkt?",
    options: [
      "Er ist ein reguläres Rechtsmittel zur inhaltlichen Überprüfung der Entscheidung der Beschwerdekammer durch eine weitere Instanz.",
      "Er dient ausschließlich der Überprüfung auf schwerwiegende Verfahrensmängel (z.B. Besetzungsmangel, Verletzung des rechtlichen Gehörs, Entscheidung über nicht gestellte Anträge) und berührt nicht die inhaltliche Richtigkeit der Entscheidung.",
      "Er kann von jeder Partei bei der Großen Beschwerdekammer eingelegt werden, um eine Neubewertung der Patentierbarkeit zu erreichen.",
      "Er ermöglicht dem EPA-Präsidenten die Vorlage von Rechtsfragen an die Große Beschwerdekammer.",
    ],
    answers: [1],
    explanation:
      "Art. 112a EPÜ: Der Überprüfungsantrag ist kein reguläres Rechtsmittel in der Sache, sondern ein außerordentlicher Rechtsbehelf gegen Entscheidungen der Beschwerdekammern bei schwerwiegenden Verfahrensmängeln. Zulässige Rügegründe sind abschließend in Art. 112a Abs. 2 EPÜ gelistet: z.B. Verletzung des rechtlichen Gehörs (Art. 112a Abs. 2(c) EPÜ), Entscheidung über nicht gestellte Anträge (Art. 112a Abs. 2(d) EPÜ) oder kriminelles Verhalten (Art. 112a Abs. 2(a/b) EPÜ). Die inhaltliche Richtigkeit der rechtlichen Beurteilung ist kein Überprüfungsgrund.",
    sourceIds: ["art112aepc"],
    sources: [
      {
        id: "art112aepc",
        label: "Art. 112a EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a112a.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  // ─── MC MULTI ──────────────────────────────────────────────────────────────

  {
    id: "eqe-einspruch-022",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "multi",
    topic: "EPÜ — Einspruch (Art. 100) — Mehrfachwahl",
    title: "Welche Aussagen zu Art. 100 EPÜ sind zutreffend?",
    prompt:
      "Welche der folgenden Aussagen zu den Einspruchsgründen nach Art. 100 EPÜ sind zutreffend? (Mehrere Antworten möglich.)",
    options: [
      "Art. 100(a) EPÜ umfasst sowohl mangelnde Neuheit (Art. 54 EPÜ) als auch mangelnde erfinderische Tätigkeit (Art. 56 EPÜ).",
      "Art. 100(b) EPÜ entspricht dem Erfordernis der ausführbaren Offenbarung nach Art. 83 EPÜ.",
      "Art. 100(c) EPÜ deckt auch Erweiterungen, die gegen Art. 123 Abs. 3 EPÜ (Schutzbereichserweiterung nach Erteilung) verstoßen.",
      "Die Einspruchsgründe nach Art. 100 EPÜ sind abschließend; weitere Gründe (z.B. Unklarheit Art. 84 EPÜ) können im Einspruch nicht selbständig geltend gemacht werden.",
    ],
    answers: [0, 1, 3],
    explanation:
      "Richtig sind die Aussagen (a), (b) und (d): Art. 100(a) EPÜ verweist auf Art. 52–57 EPÜ und umfasst damit die gesamte Patentierbarkeit einschließlich Neuheit und erfinderischer Tätigkeit. Art. 100(b) EPÜ entspricht Art. 83 EPÜ. Die Einspruchsgründe sind abschließend — Art. 84 EPÜ (Klarheit) ist kein eigenständiger Einspruchsgrund und kann nur insoweit relevant werden, als Änderungen im Einspruch neue Unklarheiten einführen. Falsch ist (c): Art. 100(c) EPÜ betrifft allein unzulässige Erweiterungen gegenüber der ursprünglichen Anmeldung (Art. 123 Abs. 2 EPÜ), nicht Verstöße gegen Art. 123 Abs. 3 EPÜ.",
    sourceIds: ["art100epc"],
    sources: [
      {
        id: "art100epc",
        label: "Art. 100 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a100.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["einspruch", "epc", "claim-analysis"],
    reviewStatus: "beta-ready",
  },

  // ─── FLASHCARDS ────────────────────────────────────────────────────────────

  {
    id: "eqe-einspruch-023",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ — Einspruch (Art. 99/100)",
    title: "Einspruch: Frist, Beteiligte, Gründe",
    prompt:
      "Nennen Sie die Einspruchsfrist nach Art. 99 EPÜ, den Kreis der Einspruchsberechtigten und die drei Einspruchsgründe des Art. 100 EPÜ.",
    options: [],
    answers: [],
    explanation:
      "Art. 99 Abs. 1 EPÜ: Die Einspruchsfrist beträgt 9 Monate ab Bekanntmachung des Hinweises auf die Erteilung im Europäischen Patentblatt. Einspruchsberechtigt ist jedermann (Popularrechtsbehelf), mit Ausnahme des Patentinhabers selbst. Art. 100 EPÜ nennt abschließend drei Gründe: (a) mangelnde Patentierbarkeit nach Art. 52–57 EPÜ; (b) mangelnde ausführbare Offenbarung nach Art. 83 EPÜ; (c) unzulässige Erweiterung gegenüber der eingereichten Anmeldung nach Art. 76 Abs. 1 / Art. 123 Abs. 2 EPÜ.",
    sourceIds: ["art99epc", "art100epc"],
    sources: [
      {
        id: "art99epc",
        label: "Art. 99 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a99.html",
      },
      {
        id: "art100epc",
        label: "Art. 100 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a100.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["einspruch", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-024",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ — Einspruchsverfahren (R. 76/77/79/81 EPÜ-AO)",
    title: "Verfahrensablauf Einspruch: R. 76–81 EPÜ-AO",
    prompt:
      "Erläutern Sie den wesentlichen Verfahrensablauf des Einspruchsverfahrens von der Einreichung bis zur Entscheidung unter Angabe der relevanten Regeln der EPÜ-Ausführungsordnung.",
    options: [],
    answers: [],
    explanation:
      "Einreichung: Einspruch + Gebühr binnen 9 Monaten (Art. 99 EPÜ, R. 76 EPÜ-AO — Mindestinhalt). Zulässigkeitsprüfung: R. 77 EPÜ-AO — formale Prüfung; unzulässige Einsprüche werden verworfen. Mitteilung an Patentinhaber: R. 79 EPÜ-AO — der Patentinhaber erhält Gelegenheit zur Stellungnahme, kann Änderungen einreichen. Sachprüfung: Art. 101 EPÜ — die Einspruchsabteilung prüft die Einspruchsgründe von Amts wegen. Verfahrensleitung: R. 81 EPÜ-AO (Verfahrensbeteiligter kann Änderungen vornehmen). Entscheidung: Art. 102 EPÜ — Widerruf, Aufrechterhaltung in geändertem Umfang oder Zurückweisung des Einspruchs.",
    sourceIds: ["r76epc", "r77epc"],
    sources: [
      {
        id: "r76epc",
        label: "R. 76 EPÜ-AO",
        url: "https://www.epo.org/en/legal/epc/2020/r76.html",
      },
      {
        id: "r77epc",
        label: "R. 77 EPÜ-AO",
        url: "https://www.epo.org/en/legal/epc/2020/r77.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["einspruch", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-025",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ — Beschwerde (Art. 106–111)",
    title: "Beschwerdefristen und Wirkungen im Überblick",
    prompt:
      "Nennen Sie die zwei Fristen des Art. 108 EPÜ für das Beschwerdeverfahren sowie die zwei typischen Grundwirkungen jeder zulässigen Beschwerde.",
    options: [],
    answers: [],
    explanation:
      "Art. 108 EPÜ: (1) Einlegungsfrist: 2 Monate ab Zustellung der angefochtenen Entscheidung; (2) Begründungsfrist: 4 Monate ab Zustellung der angefochtenen Entscheidung. Beide Fristen sind nicht verlängerbar. Wirkungen: Suspensiveffekt (Art. 106 Abs. 1 EPÜ: aufschiebende Wirkung — die Entscheidung wird nicht vollzogen) und Devolutiveffekt (die Zuständigkeit geht auf die Beschwerdekammer über). Fehlende oder verspätete Begründung führt dazu, dass die Beschwerde als nicht eingelegt gilt (R. 101 EPÜ-AO).",
    sourceIds: ["art108epc", "art106epc"],
    sources: [
      {
        id: "art108epc",
        label: "Art. 108 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a108.html",
      },
      {
        id: "art106epc",
        label: "Art. 106 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a106.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-026",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ — Reformatio in peius (G 9/92 / G 1/99)",
    title: "Reformatio in peius — Grundsatz und Ausnahme",
    prompt:
      "Was besagt der Grundsatz 'keine reformatio in peius' im EPÜ-Beschwerdeverfahren (G 9/92), und welche Ausnahme lässt G 1/99 zu?",
    options: [],
    answers: [],
    explanation:
      "G 9/92 (Große Beschwerdekammer): Legt nur eine Partei Beschwerde ein, darf die Beschwerdekammer das Ergebnis für den alleinigen Beschwerdeführer nicht verschlechtern — keine reformatio in peius. Folge: Legt nur der Patentinhaber Beschwerde ein, darf die Kammer das Patent nicht stärker beschränken als die Vorinstanz; legt nur der Einsprechende Beschwerde ein, darf die Kammer das Patent nicht breiter aufrechterhalten. G 1/99 (Ausnahme im UMGEKEHRTEN Fall): Ist der EINSPRECHENDE alleiniger Beschwerdeführer und enthält das geändert aufrechterhaltene Patent einen Art.-123(2)-Mangel, dessen bloße Streichung den Anspruch erweitern und den Einsprechenden schlechterstellen würde, darf der Patentinhaber (Beschwerdegegner) ausnahmsweise eine solche Änderung vornehmen — gestuft: zuerst ein den Anspruch beschränkendes Merkmal; nur wenn das nicht möglich ist, als letztes Mittel die Streichung des unzulässigen Merkmals (Auflösung der ‚unentrinnbaren Falle' Art. 123(2)/(3)).",
    sourceIds: ["art106epc"],
    sources: [
      {
        id: "art106epc",
        label: "Art. 106 EPÜ / G 9/92",
        url: "https://www.epo.org/en/legal/epc/2020/a106.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Ausnahme",
    skillTags: ["beschwerde", "epc"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-027",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ — Große Beschwerdekammer (Art. 112/112a)",
    title: "Große Beschwerdekammer: Vorlage vs. Überprüfungsantrag",
    prompt:
      "Worin unterscheiden sich die Vorlage nach Art. 112 EPÜ und der Überprüfungsantrag nach Art. 112a EPÜ hinsichtlich Initiator, Gegenstand und Rechtsfolge?",
    options: [],
    answers: [],
    explanation:
      "Art. 112 EPÜ (Vorlage): Initiiert durch eine Beschwerdekammer (von Amts wegen oder auf Antrag einer Partei) oder durch den EPA-Präsidenten. Gegenstand: Rechtsfragen von grundsätzlicher Bedeutung oder Divergenz zwischen Beschwerdekammern. Rechtsfolge: Bindende Entscheidung der Großen Beschwerdekammer für alle Beschwerdekammern. Art. 112a EPÜ (Überprüfungsantrag): Initiiert durch eine Partei des abgeschlossenen Beschwerdeverfahrens. Gegenstand: ausschließlich abschließend aufgezählte schwerwiegende Verfahrensmängel (kein Sachfehler). Rechtsfolge: Aufhebung der Entscheidung und Rückverweisung bei Begründetheit; keine inhaltliche Neubewertung.",
    sourceIds: ["art112epc", "art112aepc"],
    sources: [
      {
        id: "art112epc",
        label: "Art. 112 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a112.html",
      },
      {
        id: "art112aepc",
        label: "Art. 112a EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a112a.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-028",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ — Einspruch (Art. 105a–c) — Beschränkung auf Antrag",
    title: "Beschränkung/Widerruf auf Antrag (Art. 105a–c EPÜ)",
    prompt:
      "Erläutern Sie das Verfahren nach Art. 105a–c EPÜ: Wer kann es einleiten, was wird geprüft, und welche Wirkung hat eine bewilligte Beschränkung?",
    options: [],
    answers: [],
    explanation:
      "Art. 105a Abs. 1 EPÜ: Nur der Patentinhaber kann beim EPA die Beschränkung oder den Widerruf des Patents beantragen. Art. 105a Abs. 2 EPÜ: Während eines laufenden Einspruchsverfahrens ist das Verfahren nicht zulässig. Art. 105b EPÜ: Das EPA prüft, ob der Beschränkungsantrag den Erfordernissen des EPÜ entspricht (insb. Art. 123 Abs. 2 und 3 EPÜ); ist dies der Fall, beschließt es die Beschränkung. Die Beschränkung wirkt auf alle Vertragsstaaten, für die das Patent erteilt wurde, und tritt mit dem Tag der Bekanntmachung des Hinweises im Europäischen Patentblatt ein (Art. 105b Abs. 3 EPÜ). Art. 105c EPÜ regelt die Veröffentlichung der geänderten Patentschrift.",
    sourceIds: ["art105aepc"],
    sources: [
      {
        id: "art105aepc",
        label: "Art. 105a EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a105a.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["einspruch", "epc"],
    // FIX (question-reviewer 2026-06-03): Sub-Zitat geglättet — die Wirkung der
    // Beschränkung ab Bekanntmachung folgt aus Art. 105b Abs. 3 EPÜ (war fälschlich Art.
    // 105c zugeordnet; Art. 105c regelt die Veröffentlichung der geänderten Patentschrift).
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-029",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ — Beschwerdeberechtigung und Inhalt (Art. 107 / R. 99)",
    title: "Beschwerdeberechtigung und R. 99 EPÜ-AO — Inhalt der Beschwerdeschrift",
    prompt:
      "Welche formalen Anforderungen stellt R. 99 EPÜ-AO an die Beschwerdeschrift, und welche Voraussetzungen muss der Beschwerdeführer nach Art. 107 EPÜ erfüllen?",
    options: [],
    answers: [],
    explanation:
      "Art. 107 EPÜ: Beschwerdebefugt ist jede Partei des vorangegangenen Verfahrens, soweit sie durch die Entscheidung beschwert ist. R. 99 Abs. 1 EPÜ-AO: Die Beschwerdeschrift muss enthalten: (a) Bezeichnung der angefochtenen Entscheidung; (b) Name und Anschrift des Beschwerdeführers; (c) eine Erklärung, in welchem Umfang die Entscheidung angefochten wird und welche Abänderung beantragt wird. R. 99 Abs. 2 EPÜ-AO: Die Beschwerdebegründung muss die vollständigen Gründe angeben, auf die sich die Beschwerde stützt. Fehlen wesentliche Angaben, wird die Beschwerde als unzulässig verworfen (R. 101 EPÜ-AO).",
    sourceIds: ["art107epc", "r99epc"],
    sources: [
      {
        id: "art107epc",
        label: "Art. 107 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a107.html",
      },
      {
        id: "r99epc",
        label: "R. 99 EPÜ-AO",
        url: "https://www.epo.org/en/legal/epc/2020/r99.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  // ─── ORDERING ──────────────────────────────────────────────────────────────

  {
    id: "eqe-einspruch-030",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ — Einspruchsverfahren — Ablauf",
    title: "Ablauf des Einspruchsverfahrens (Prüfungsschema)",
    prompt:
      "Bringe die Schritte des EPÜ-Einspruchsverfahrens in die richtige chronologische Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Einlegung des Einspruchs + Zahlung der Einspruchsgebühr innerhalb der 9-Monats-Frist (Art. 99 EPÜ, R. 76 EPÜ-AO)",
      "Zulässigkeitsprüfung durch die Einspruchsabteilung — Verwerfung bei Formmangel (R. 77 EPÜ-AO)",
      "Mitteilung des Einspruchs an den Patentinhaber + Möglichkeit zur Stellungnahme und Änderung (R. 79/81 EPÜ-AO, Art. 101 Abs. 2 EPÜ)",
      "Sachprüfung der Einspruchsgründe nach Art. 100 EPÜ (ggf. mündliche Verhandlung)",
      "Entscheidung der Einspruchsabteilung: Widerruf, Aufrechterhaltung in geändertem Umfang oder Zurückweisung (Art. 101/102 EPÜ)",
    ],
    explanation:
      "Das Einspruchsverfahren folgt einer klaren Kaskade: (1) Fristgerechte Einreichung mit Einspruchsgebühr — die 9-Monats-Frist ist absolut und nicht verlängerbar. (2) Formale Zulässigkeitsprüfung (R. 77 EPÜ-AO) — fehlende Angaben zu Gründen oder Fakten führen zur Verwerfung als unzulässig. (3) Mitteilung + Stellungnahme (R. 79/81 EPÜ-AO) — der Patentinhaber kann das Patent anpassen. (4) Sachprüfung von Amts wegen (Art. 101 EPÜ). (5) Entscheidung nach Art. 102 EPÜ.",
    sourceIds: ["art99epc", "art101epc", "art102epc", "r76epc"],
    sources: [
      {
        id: "art99epc",
        label: "Art. 99 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a99.html",
      },
      {
        id: "art101epc",
        label: "Art. 101 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a101.html",
      },
      {
        id: "art102epc",
        label: "Art. 102 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a102.html",
      },
      {
        id: "r76epc",
        label: "R. 76 EPÜ-AO",
        url: "https://www.epo.org/en/legal/epc/2020/r76.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["einspruch", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-031",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ — Beschwerdeverfahren — Ablauf",
    title: "Ablauf des Beschwerdeverfahrens (Prüfungsschema)",
    prompt:
      "Bringe die Schritte des EPÜ-Beschwerdeverfahrens in die richtige chronologische Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Einlegung der Beschwerde + Zahlung der Beschwerdegebühr innerhalb von 2 Monaten nach Zustellung der Entscheidung (Art. 108 S. 1/2 EPÜ)",
      "Vorlage der Beschwerdebegründung innerhalb von 4 Monaten nach Zustellung der Entscheidung (Art. 108 S. 3 EPÜ)",
      "Prüfung der Abhilfemöglichkeit durch die Vorinstanz innerhalb von 3 Monaten (Art. 109 Abs. 1 EPÜ) — bei Nichtabhilfe: Vorlage an Beschwerdekammer",
      "Prüfung der Zulässigkeit und Begründetheit durch die Beschwerdekammer (Art. 110/111 EPÜ, R. 100 EPÜ-AO)",
      "Entscheidung der Beschwerdekammer: Sachentscheidung oder Zurückverweisung (Art. 111 EPÜ)",
    ],
    explanation:
      "Das Beschwerdeverfahren: (1) 2-Monats-Frist für die Beschwerdeschrift (Art. 108 S. 1/2 EPÜ). (2) 4-Monats-Frist für die Begründung (Art. 108 S. 3 EPÜ) — beide ab Zustellung der angefochtenen Entscheidung. (3) Abhilfeverfahren (Art. 109 EPÜ): Die Vorinstanz kann innerhalb von 3 Monaten selbst abhelfen; tut sie es nicht, wird vorgelegt. (4) Kammer prüft Zulässigkeit und Begründetheit. (5) Entscheidung nach Art. 111 EPÜ: Sachentscheidung oder Zurückverweisung, jeweils mit bindenden Rechtsausführungen bei Zurückverweisung (Art. 111 Abs. 2 EPÜ).",
    sourceIds: ["art108epc", "art109epc", "art111epc"],
    sources: [
      {
        id: "art108epc",
        label: "Art. 108 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a108.html",
      },
      {
        id: "art109epc",
        label: "Art. 109 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a109.html",
      },
      {
        id: "art111epc",
        label: "Art. 111 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a111.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-032",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ — Einspruchsgründe — Prüfungsreihenfolge (Art. 100)",
    title: "Prüfungsreihenfolge der Einspruchsgründe im Angriff",
    prompt:
      "Ein Angreifer (Einsprechender) möchte ein Patent möglichst effektiv angreifen. Ordne die Prüfschritte für die Einspruchsgründe in der sinnvollen gutachterlichen Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Art. 100(c) EPÜ: Prüfe unzulässige Erweiterung — Vergleich erteilter Anspruch vs. ursprüngliche Anmeldung (Art. 123 Abs. 2 EPÜ / Art. 76 Abs. 1 EPÜ bei Teilanmeldung)",
      "Art. 100(b) EPÜ: Prüfe Ausführbarkeit — kann ein Fachmann die Erfindung über den gesamten Schutzbereich hinweg reproduzieren? (Art. 83 EPÜ)",
      "Art. 100(a) EPÜ i.V.m. Art. 54 EPÜ: Prüfe Neuheit — wird jedes Merkmal von Anspruch 1 von einem einzigen Stand-der-Technik-Dokument vorweggenommen?",
      "Art. 100(a) EPÜ i.V.m. Art. 56 EPÜ: Prüfe erfinderische Tätigkeit — Aufgabe-Lösungs-Ansatz ausgehend vom nächstliegenden Stand der Technik",
    ],
    explanation:
      "Im M3-'Attack'-Ansatz empfiehlt sich die Reihenfolge: Erst formale/strukturelle Einwände (Art. 100(c) — unzulässige Erweiterung), dann Ausführbarkeit (Art. 100(b)), dann Neuheit (Art. 100(a)/Art. 54), dann erfinderische Tätigkeit (Art. 100(a)/Art. 56). Unzulässige Erweiterung ist oft 'fatal' für alle Ansprüche und erfordert keine Prior-Art-Suche. Neuheit ist strenger (ein Dokument) und wird vor erfinderischer Tätigkeit geprüft (Kombination). Dieser Aufbau entspricht der Prüflogik der EPA-Richtlinien (Guidelines Part D).",
    sourceIds: ["art100epc", "art83epc", "art54epc"],
    sources: [
      {
        id: "art100epc",
        label: "Art. 100 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a100.html",
      },
      {
        id: "art83epc",
        label: "Art. 83 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a83.html",
      },
      {
        id: "art54epc",
        label: "Art. 54 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a54.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["einspruch", "epc", "claim-analysis"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-033",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ — Prüfung der Beschwerdezulässigkeit",
    title: "Prüfschema Zulässigkeit der Beschwerde",
    prompt:
      "Ordne die Prüfschritte zur Zulässigkeit einer Beschwerde im EPÜ-Verfahren in der richtigen Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Parteifähigkeit und Parteistellung: War der Beschwerdeführer Partei des vorangegangenen Verfahrens? (Art. 107 EPÜ)",
      "Beschwer: Ist der Beschwerdeführer durch die angefochtene Entscheidung beschwert? (Art. 107 EPÜ)",
      "Einhaltung der 2-Monats-Frist für die Beschwerdeschrift und Zahlung der Gebühr (Art. 108 S. 1/2 EPÜ)",
      "Einhaltung der 4-Monats-Frist und Vollständigkeit der Beschwerdebegründung (Art. 108 S. 3 EPÜ, R. 99 Abs. 2 EPÜ-AO)",
    ],
    explanation:
      "Die Zulässigkeitsprüfung der Beschwerde erfolgt in vier Stufen: (1) Parteistellung nach Art. 107 EPÜ — nur Verfahrensparteien sind beschwerdebefugt. (2) Beschwer — die Entscheidung muss dem Beschwerdeführer zumindest teilweise nachteilig sein. (3) Formale 2-Monats-Frist und Gebühr (Art. 108 S. 1/2 EPÜ) — bei Versäumnis ist die Beschwerde als unzulässig zu verwerfen. (4) Materielle Begründung innerhalb von 4 Monaten (Art. 108 S. 3 EPÜ) — ohne Begründung gilt die Beschwerde als nicht eingelegt. Alle vier Voraussetzungen müssen kumulativ erfüllt sein.",
    sourceIds: ["art107epc", "art108epc"],
    sources: [
      {
        id: "art107epc",
        label: "Art. 107 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a107.html",
      },
      {
        id: "art108epc",
        label: "Art. 108 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a108.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["beschwerde", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-034",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ — Reformatio in peius — G 1/99 (gestufte Änderung)",
    title: "G 1/99 — gestufte Änderungsreihenfolge in der unentrinnbaren Falle",
    prompt:
      "Der Einsprechende ist alleiniger Beschwerdeführer; das von der Einspruchsabteilung geändert aufrechterhaltene Patent enthält einen Art.-123(2)-Mangel, dessen bloße Streichung den Schutzbereich erweitern würde. Ordne die nach G 1/99 zulässigen Reaktionen des Patentinhabers in der vorgeschriebenen Reihenfolge (vorrangig → nachrangig).",
    options: [],
    answers: [],
    orderItems: [
      "Änderung durch Aufnahme eines den Anspruch weiter beschränkenden Merkmals (beseitigt den Mangel ohne Schlechterstellung des Einsprechenden)",
      "Änderung, die den Schutzbereich des aufrechterhaltenen Patents nicht erweitert (auch wenn der Einwand dadurch nicht vollständig ausgeräumt wird)",
      "Als letztes Mittel: Streichung des unzulässigen Merkmals, auch wenn dadurch der Schutzbereich erweitert und der Einsprechende schlechtergestellt wird",
    ],
    explanation:
      "G 1/99 löst die ‚unentrinnbare Falle' (Konflikt Art. 123(2) ./. Art. 123(3)) im Fall des allein beschwerdeführenden Einsprechenden: Grundsätzlich verbietet das Verschlechterungsverbot (G 9/92) dem Patentinhaber als Beschwerdegegner eine den Einsprechenden schlechterstellende Erweiterung. G 1/99 erlaubt sie ausnahmsweise, jedoch nur in dieser strikten Reihenfolge — zuerst ein beschränkendes Merkmal, dann eine schutzbereichsneutrale Änderung, und nur als letztes Mittel die Streichung des unzulässigen Merkmals.",
    sourceIds: ["art123epc"],
    sources: [
      {
        id: "art123epc",
        label: "Art. 123 EPÜ / G 1/99",
        url: "https://www.epo.org/en/legal/epc/2020/a123.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["beschwerde", "epc", "reformatio-in-peius"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  // ─── CLOZE ─────────────────────────────────────────────────────────────────

  {
    id: "eqe-einspruch-035",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ — Einspruch (Art. 99) — Frist",
    title: "Cloze: Einspruchsfrist Art. 99 EPÜ",
    prompt:
      "Nach Art. 99 Abs. 1 EPÜ kann ein Einspruch innerhalb von ___ Monaten ab der Bekanntmachung des Hinweises auf die Erteilung im Europäischen Patentblatt eingelegt werden.",
    options: ["3", "6", "9", "12"],
    answers: [2],
    explanation:
      "Art. 99 Abs. 1 EPÜ: Die Einspruchsfrist beträgt neun Monate. Sie ist absolut und nicht verlängerbar. Beginn: Bekanntmachung des Hinweises auf die Erteilung im Europäischen Patentblatt (nicht: Tag der Patenterteilung oder Zustellung an den Inhaber).",
    sourceIds: ["art99epc"],
    sources: [
      {
        id: "art99epc",
        label: "Art. 99 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a99.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["einspruch", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-036",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ — Beschwerde (Art. 108) — Einlegungsfrist",
    title: "Cloze: Beschwerdeeinlegungsfrist Art. 108 EPÜ",
    prompt:
      "Nach Art. 108 EPÜ ist die Beschwerde innerhalb von ___ Monaten nach Zustellung der angefochtenen Entscheidung einzulegen.",
    options: ["1", "2", "3", "4"],
    answers: [1],
    explanation:
      "Art. 108 S. 1 EPÜ: Die Einlegungsfrist beträgt zwei Monate ab Zustellung der Entscheidung. Gleichzeitig muss die Beschwerdegebühr entrichtet werden (Art. 108 S. 2 EPÜ). Diese Frist ist nicht verlängerbar; fehlt die Zahlung oder die Einlegungserklärung, gilt die Beschwerde als nicht eingelegt.",
    sourceIds: ["art108epc"],
    sources: [
      {
        id: "art108epc",
        label: "Art. 108 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a108.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-037",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ — Beschwerde (Art. 108) — Begründungsfrist",
    title: "Cloze: Beschwerdebegründungsfrist Art. 108 EPÜ",
    prompt:
      "Die Beschwerdebegründung nach Art. 108 EPÜ ist innerhalb von ___ Monaten nach Zustellung der angefochtenen Entscheidung einzureichen.",
    options: ["2", "3", "4", "6"],
    answers: [2],
    explanation:
      "Art. 108 S. 3 EPÜ: Die Begründungsfrist beträgt vier Monate ab Zustellung der Entscheidung. Diese Frist ist nicht verlängerbar und unabhängig von der Einlegungsfrist (die beide ab Zustellung der Entscheidung laufen). Eine fehlende oder unvollständige Begründung führt dazu, dass die Beschwerde als nicht eingelegt gilt.",
    sourceIds: ["art108epc"],
    sources: [
      {
        id: "art108epc",
        label: "Art. 108 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a108.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-038",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ — Beschwerde (Art. 109) — Abhilfefrist",
    title: "Cloze: Abhilfefrist Art. 109 EPÜ",
    prompt:
      "Nach Art. 109 Abs. 1 EPÜ kann die Vorinstanz der Beschwerde innerhalb von ___ Monaten nach Eingang der Beschwerdebegründung abhelfen.",
    options: ["1", "2", "3", "6"],
    answers: [2],
    explanation:
      "Art. 109 Abs. 1 EPÜ: Die Vorinstanz (z.B. Einspruchsabteilung) hat drei Monate nach Eingang der Beschwerdebegründung Zeit, um der Beschwerde abzuhelfen (Abhilfe/Selbsthilfe). Hilft sie nicht ab, muss sie die Akte unverzüglich der Beschwerdekammer vorlegen (Art. 109 Abs. 2 EPÜ). Dieses Abhilfeverfahren ist ein wichtiger Effizienzfilter, bevor die Beschwerdekammer tätig wird.",
    sourceIds: ["art109epc"],
    sources: [
      {
        id: "art109epc",
        label: "Art. 109 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a109.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["beschwerde", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-einspruch-039",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ — Beitritt (Art. 105) — Beitrittsfrist",
    title: "Cloze: Beitrittsfrist Art. 105 EPÜ",
    prompt:
      "Nach Art. 105 Abs. 2 EPÜ muss der Beitritt zum Einspruchsverfahren innerhalb von ___ Monaten nach dem Datum eingereicht werden, an dem die Klage erhoben wurde oder die Unterlassungsaufforderung ergangen ist.",
    options: ["1", "3", "6", "9"],
    answers: [1],
    explanation:
      "Art. 105 Abs. 2 EPÜ: Die Beitrittsfrist beträgt drei Monate ab dem Datum der Erhebung der Verletzungsklage oder der Unterlassungsaufforderung an den Dritten. Der Beitritt nach Art. 105 EPÜ setzt außerdem voraus, dass noch ein Einspruchsverfahren anhängig ist und eine der in Art. 105 Abs. 1 EPÜ genannten Voraussetzungen vorliegt.",
    sourceIds: ["art105epc"],
    sources: [
      {
        id: "art105epc",
        label: "Art. 105 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a105.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["einspruch", "fristen-prioritaet", "epc"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE — Fallkonstellation (Beschwerde) ───────────────────────────

  {
    id: "eqe-einspruch-040",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ — Beschwerde — Fallkonstellation — Prüfungsschema",
    title: "Fall: Komplexes Einspruchs-/Beschwerdeverfahren — Strategieentscheidung",
    prompt:
      "Patentinhaber P hat gegen das Widerrufsurteil der Einspruchsabteilung Beschwerde eingelegt. Einsprechender E hat keine Beschwerde eingelegt. In der mündlichen Verhandlung vor der Beschwerdekammer stellt P einen neuen Hilfsantrag vor, der das Patent stärker beschränkt als die von der Einspruchsabteilung widerrufene Fassung. Welche Aussage ist richtig?",
    options: [
      "Die Beschwerdekammer muss den Hilfsantrag ablehnen, weil er außerhalb des Beschwerdegegenstands liegt.",
      "Die Beschwerdekammer kann dem Hilfsantrag stattgeben: P ist alleiniger Beschwerdeführer und darf sein eigenes Patent jederzeit freiwillig weiter beschränken — das Verbot der reformatio in peius schützt nur ihn und steht einer selbst beantragten Einschränkung nicht entgegen (kein G-1/99-Fall).",
      "Die Beschwerdekammer muss das Patent vollständig aufrechterhalten, weil nur P Beschwerde eingelegt hat und eine weitere Einschränkung eine reformatio in peius darstellt.",
      "Die Beschwerdekammer muss die Sache an die Einspruchsabteilung zurückverweisen, weil sie keine eigene Sachentscheidung zu neuen Hilfsanträgen treffen darf.",
    ],
    answers: [1],
    explanation:
      "Das Verbot der reformatio in peius (G 9/92) schützt den alleinigen Beschwerdeführer vor unfreiwilliger Schlechterstellung. Ist — wie hier — der PATENTINHABER alleiniger Beschwerdeführer, kann er sein Patent jederzeit freiwillig weiter beschränken; eine von ihm selbst beantragte engere Fassung ist schlicht sein eigener Antrag und bedarf keiner Ausnahme. G 1/99 betrifft den UMGEKEHRTEN Fall (der Einsprechende ist alleiniger Beschwerdeführer) und erlaubt dem Patentinhaber dort ausnahmsweise eine den Einsprechenden schlechterstellende Änderung in gestufter Reihenfolge. Art. 111 Abs. 1 EPÜ gibt der Kammer die Sachkompetenz für die Entscheidung.",
    sourceIds: ["art111epc", "art106epc"],
    sources: [
      {
        id: "art111epc",
        label: "Art. 111 EPÜ",
        url: "https://www.epo.org/en/legal/epc/2020/a111.html",
      },
      {
        id: "art106epc",
        label: "Art. 106 EPÜ / G 9/92 / G 1/99",
        url: "https://www.epo.org/en/legal/epc/2020/a106.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["beschwerde", "einspruch", "epc", "claim-analysis"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },
];
